import { Img } from "@/components/site/Img";
import { useEffect, useState } from "react";
import { ArrowRight, Phone, Play, Star } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { VideoLightbox } from "./VideoLightbox";
import { useSmsHref } from "./ContactButtons";
import { MESSENGER_URL, PHONE_HREF, PHONE_DISPLAY, SMS_DRAFTS } from "@/lib/contact";
import { track } from "@/lib/track";

const HERO_QUOTES = [
  { text: "Excellent communication! Delivers on time and I looove my angels!", name: "Judith" },
  { text: "The birdbath was exactly as described. He made a new one just for me!", name: "Demetra" },
  { text: "I would buy again. Honest and prices are fair.", name: "John" },
  { text: "Chris is a really nice guy, it's worth the trip to Villa Rica.", name: "Clint" },
  { text: "Solid, easy to work with, knowledgeable and all around great guy!", name: "Derek" },
];

const walkthroughVideo = {
  mp4: `${process.env.REACT_APP_BACKEND_URL}/api/media/garden-walkthrough.mp4`,
  poster: "/images/walkthrough-poster.jpg",
  title: "The full garden walkthrough",
  portrait: true,
};

const FountainIcon = () => (
  <svg viewBox="0 0 48 56" width="44" height="52" aria-hidden className="flex-shrink-0 text-[#C8B387]" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="24" cy="5" r="3" fill="currentColor" stroke="none" />
    <path d="M24 8v4" />
    <path d="M14 14h20l-3 5H17z" />
    <path d="M24 19v5" />
    <path d="M9 26h30l-4 6H13z" />
    <path d="M24 32v6" />
    <path d="M3 40h42l-3 7H6z" />
    <path d="M10 47v5M38 47v5M24 47v5" />
    <path d="M20 12c-2 3-2 5 0 7M28 12c2 3 2 5 0 7" strokeDasharray="1 2" />
    <path d="M17 24c-3 2-3 5 0 8M31 24c3 2 3 5 0 8" strokeDasharray="1 2" />
  </svg>
);

export default function Hero() {
  const heroSms = useSmsHref(SMS_DRAFTS.general);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [walkthroughAutoplay, setWalkthroughAutoplay] = useState(true);
  const [quoteIndex, setQuoteIndex] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setQuoteIndex((i) => (i + 1) % HERO_QUOTES.length), 7000);
    return () => clearInterval(t);
  }, []);
  const { hash, pathname, search } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (hash === "#walkthrough") {
      setWalkthroughAutoplay(false);
      setShowWalkthrough(true);
      track("walkthrough_hash_open", "Garden Tour link");
    }
  }, [hash]);
  const closeWalkthrough = () => {
    setShowWalkthrough(false);
    if (hash === "#walkthrough") navigate(pathname + search, { replace: true });
  };
  return (
    <section
      data-testid="hero-section"
      id="top"
      className="relative w-full overflow-hidden grain bg-[#1F1E1C]"
    >
      <div className="flex flex-col lg:flex-row lg:min-h-[92vh]">
        <div className="contents lg:flex lg:w-[47%] lg:items-center">
          <div className="contents lg:block lg:w-full lg:px-14 lg:py-24 lg:max-w-[760px] lg:mx-auto">
            <div data-testid="hero-primary-block" className="order-1 w-full px-6 md:px-10 pt-24 md:pt-32 pb-8 md:pb-10 md:max-w-[760px] md:mx-auto lg:p-0 lg:max-w-none">
            <div className="fade-up section-eyebrow !text-[#E8DFC9] mb-4 md:mb-5">
              <span><span className="hidden sm:inline">Villa Rica, GA · </span>Serving the United States</span>
            </div>
            <h1
              data-testid="hero-headline"
              className="fade-up delay-1 text-white font-serif font-light leading-[1.04] md:leading-[1.05] tracking-tight text-[2.5rem] sm:text-5xl md:text-[3.5rem] xl:text-6xl"
            >
              Fountains built by hand,{" "}
              <br className="hidden sm:block" />
              <em className="not-italic text-[#E8DFC9]">kept running for years.</em>
            </h1>

            <p
              data-testid="hero-subtext"
              className="fade-up delay-2 mt-5 md:mt-6 text-base md:text-lg text-white/85 leading-relaxed font-light max-w-xl"
            >
              Handcrafted fountains and garden pieces, made in Villa Rica. We
              will come to you: repairs, cleanings, and deliveries available
              within the United States for a small fee.
            </p>

            <div className="fade-up delay-3 mt-7 md:mt-8 grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:gap-4">
              <a
                href={PHONE_HREF}
                onClick={() => track("call_click", "Hero")}
                data-testid="hero-cta-primary"
                aria-label={`Call Chris at ${PHONE_DISPLAY}`}
                className="btn-primary sm:whitespace-nowrap !px-4 sm:!px-7"
              >
                <Phone size={15} strokeWidth={1.5} aria-hidden="true" />
                Call Chris
                <span className="hidden md:inline opacity-80 tracking-[0.1em]">· {PHONE_DISPLAY}</span>
              </a>
              <a
                href={heroSms}
                onClick={() => track("text_click", "Hero")}
                data-testid="hero-cta-text"
                aria-label={`Text Chris at ${PHONE_DISPLAY}`}
                className="btn-outline !text-[#F3EDDF] !bg-white/5 hover:!bg-[#7A6547] sm:whitespace-nowrap !px-4 sm:!px-7"
              >
                Text Chris
                <ArrowRight size={16} strokeWidth={1.5} />
              </a>
              <Link
                to="/available-now"
                data-testid="hero-cta-secondary"
                className="btn-outline !text-[#F3EDDF] !bg-white/5 hover:!bg-[#7A6547] sm:whitespace-nowrap col-span-2"
              >
                Browse Available Pieces
                <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
            <p data-testid="hero-messenger-note" className="fade-up delay-3 mt-3 text-[12px] text-white/65 leading-snug">
              Text Chris about a piece or a repair. You can attach a photo in your messaging app.{" "}
              <a
                href={MESSENGER_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("messenger_click", "Hero")}
                data-testid="hero-messenger-link"
                className="underline underline-offset-2 hover:text-white"
              >
                Prefer Facebook Messenger?
              </a>
            </p>
            <Link
              to="/majestic"
              data-testid="hero-custom-link"
              className="fade-up delay-3 mt-6 md:mt-7 group flex items-center gap-4 border border-[#C8B387]/50 hover:border-[#C8B387] bg-white/[0.04] hover:bg-white/[0.08] px-4 py-3 sm:max-w-[420px] transition-colors"
            >
              <FountainIcon />
              <span className="flex-1 min-w-0">
                <span className="block text-[#F3EDDF] text-[12px] tracking-[0.2em] uppercase">Customize a Majestic</span>
                <span className="block text-white/70 text-sm leading-snug">Pick the topper, statues, and color. Send it to Chris.</span>
              </span>
              <ArrowRight size={16} strokeWidth={1.5} className="text-[#C8B387] group-hover:translate-x-1 transition-transform flex-shrink-0" />
            </Link>
            </div>

            <div data-testid="hero-secondary-block" className="order-3 w-full px-6 md:px-10 pb-10 md:pb-16 pt-8 lg:pt-0 md:max-w-[760px] md:mx-auto lg:p-0 lg:max-w-none">

            <a
              href="#reviews"
              data-testid="hero-review-quote"
              className="fade-up delay-3 mt-6 md:mt-8 flex items-start gap-3 group max-w-xl"
            >
              <span className="flex gap-0.5 mt-1 flex-shrink-0">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={12} className="text-[#C8B387] fill-[#C8B387]" />
                ))}
              </span>
              <span
                key={quoteIndex}
                className="quote-fade text-white/75 text-sm leading-relaxed font-light italic group-hover:text-white transition-colors"
              >
                &ldquo;{HERO_QUOTES[quoteIndex].text}&rdquo;
                <span className="not-italic text-[#C8B387]">
                  {" "}— {HERO_QUOTES[quoteIndex].name}, a happy customer
                </span>
              </span>
            </a>

            <div className="fade-up delay-3 mt-4 md:mt-7 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-x-8 sm:gap-y-3">
              <a
                href="#how-its-built"
                data-testid="hero-how-built-link"
                className="inline-flex items-center gap-2 min-h-[44px] text-[13px] uppercase tracking-[0.18em] text-[#C8B387] hover:text-[#E8DFC9] transition-colors sm:whitespace-nowrap text-left scroll-mt-32"
              >
                See how it&rsquo;s built
                <ArrowRight size={14} strokeWidth={1.5} />
              </a>
              <button
                type="button"
                id="walkthrough"
                onClick={() => {
                  setWalkthroughAutoplay(true);
                  setShowWalkthrough(true);
                }}
                data-testid="hero-walkthrough-link"
                className="inline-flex items-center gap-2 min-h-[44px] text-[13px] uppercase tracking-[0.18em] text-[#C8B387] hover:text-[#E8DFC9] transition-colors sm:whitespace-nowrap text-left scroll-mt-32"
              >
                Watch the garden walkthrough
                <Play size={13} strokeWidth={1.5} fill="currentColor" />
              </button>
            </div>
            </div>
          </div>
        </div>

        <Link
          to="/builds#the-bastille"
          data-testid="hero-image-link"
          className="order-2 lg:order-none relative w-full lg:w-[53%] aspect-[4/5] lg:aspect-auto lg:min-h-[92vh] overflow-hidden block group"
        >
          <Img
            src="/images/hero-column-fountain.jpg"
            alt="The Bastille, a custom handcrafted French village style garden fountain by Garden Fountains Yard Sculptures in Villa Rica, Georgia."
            className="hero-kenburns absolute inset-0 w-full h-full object-cover object-center"
            data-testid="hero-image"
            sizes="(min-width: 1024px) 53vw, 100vw"
            fetchPriority="high"
            decoding="sync"
          />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1F1E1C]/85 to-transparent pt-16 pb-5 px-6 md:px-8">
            <p
              data-testid="hero-image-caption"
              className="text-[#C8B387] text-[12px] tracking-[0.14em] uppercase leading-relaxed max-w-xl"
            >
              The Bastille, a custom handcrafted French village style garden fountain
            </p>
            <span className="mt-2 inline-flex items-center gap-2 text-[#F9F6F0] group-hover:text-[#C8B387] text-[11px] tracking-[0.22em] uppercase transition-colors">
              See how it was built
              <ArrowRight size={13} strokeWidth={1.5} />
            </span>
          </div>
        </Link>
      </div>

      <div className="absolute bottom-6 right-6 md:right-10 z-10 text-white/70 text-[10px] tracking-[0.32em] uppercase rotate-90 origin-bottom-right hidden md:block">
        <span data-testid="hero-side-meta">Est. by Chris · Villa Rica</span>
      </div>

      <VideoLightbox
        autoplay={walkthroughAutoplay}
        open={showWalkthrough}
        onClose={closeWalkthrough}
        video={walkthroughVideo}
      />
    </section>
  );
}
